// Vendors
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';
import classNames from 'classnames';
import { FieldErrors, UseFormRegister } from 'react-hook-form';
// Context
import { MessagingState } from '@context/Context';
// Types
import { NewDiscussionThreadForm } from '@components/NewDiscussionThread/NewDiscussionThread';
// Payloads
import { IDiscussionThreadSelectPayload } from '@root/services/payloads/servicePayloads';

interface ITitleAndMessageProps {
    register: UseFormRegister<NewDiscussionThreadForm>;
    errors: FieldErrors<NewDiscussionThreadForm>;
    setMessage: (message: IDiscussionThreadSelectPayload) => void;
    amplitudeProps: { guid_id: string };
}

const TitleAndMessage = ({ register, errors, setMessage, amplitudeProps }: ITitleAndMessageProps) => {
    const { t } = useTranslation();
    const {
        state: { message },
    } = MessagingState();

    const trackFocus = (event: React.FocusEvent<Element>) => {
        amplitude.track(event.target.id + '_focused', amplitudeProps);
    };

    const trackBlur = (event: React.FocusEvent<Element>) => {
        amplitude.track(event.target.id + '_unfocused', {
            ...amplitudeProps,
            new_message_length: message.message.length,
            new_message_title_length: message.title.length,
        });
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const name = e.target.name;
        const value = e.target.value;
        setMessage({ ...message, [name]: value });
    };

    return (
        <>
            <div className="row">
                <label
                    htmlFor="title"
                    className="col-lg-2">
                    {t('headers.title')}:
                </label>
                <div className="col-lg-10 col-md-12">
                    <input
                        {...register('title', {
                            onBlur: e => {
                                trackBlur(e);
                            },
                        })}
                        type="text"
                        id="title_field"
                        aria-invalid={errors.title ? 'true' : 'false'}
                        className={errors.title ? 'has-error' : ''}
                        value={message.title}
                        onChange={onChange}
                        onFocus={e => trackFocus(e)}
                        placeholder={t('placeholders.title')}
                    />
                </div>
            </div>
            <div
                className="row justify-content-center flex-grow-1"
                id="discussion-thread-message-textarea">
                <label
                    htmlFor="message"
                    aria-label={t('inputs.messageTextarea')}
                />
                <div className="col-md-12">
                    <textarea
                        {...register('message', {
                            required: t('errors.requiredError.messageError'),
                            onBlur: e => {
                                trackBlur(e);
                            },
                        })}
                        aria-invalid={errors.message ? 'true' : 'false'}
                        aria-label={t('inputs.messageTextarea')}
                        className={classNames('h-100 resize-none', 'message-full-height', {
                            'has-error': errors.message,
                        })}
                        id="message_field"
                        value={message.message}
                        onChange={onChange}
                        onFocus={e => trackFocus(e)}
                        placeholder={t('placeholders.message')}
                    />
                    {<p className="error-text">{errors.message?.message}</p>}
                </div>
            </div>
        </>
    );
};

export default TitleAndMessage;
